import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { useCashbookStatus } from "../../helpers/cashbookStatus";
import { getPayAccount, voidPayEntry } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import MoneySheet, { MoneyMode } from "./pay/MoneySheet";
import PayLedger from "./pay/PayLedger";
import NotCounted from "./pay/NotCounted";
import "./ui/karkhana-history.scss";
import { KIND_LABEL } from "./pay/payModel";
import { UNIT_SHORT } from "./jobs/jobModel";
import { hours } from "./punches/punchModel";
import { readRoster, refreshRoster } from "./punches/roster";
import { fmtDay, rupees, shiftDay, todayIso } from "./rates/rateModel";
import PersonPicker, { Person } from "./rounds/PersonPicker";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./punches/karkhana-punches.scss";
import "./pay/karkhana-pay.scss";
import { LangToggle, useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import TakeBackDialog from "./pay/TakeBackDialog";
import CashCard from "./pay/CashCard";
import LastSheetCard from "./pay/LastSheetCard";
import PayWeekTable from "./pay/PayWeekTable";
import { useWidthTier } from "./daily/useWidthTier";
import "./daily/karkhana-daily.scss";
import { rangeLabel } from "./daily/dailyModel";
import { kp } from "./karkhanaPaths";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

/** One person's pay account: wages due, advance owed, what they earned day by day, and money given. */
const KarkhanaPayPage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const location = useLocation();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const params = new URLSearchParams(location.search);
  const [roster, setRoster] = useState(() => readRoster(orgId));
  const [person, setPerson] = useState<Person | null>(null);
  const [personId, setPersonId] = useState<string | null>(params.get("person"));
  const [to, setTo] = useState(todayIso());
  const [from, setFrom] = useState(shiftDay(todayIso(), -6));
  const [account, setAccount] = useState<any>(null);
  const [money, setMoney] = useState<MoneyMode | null>(null);
  const [ledgerKey, setLedgerKey] = useState(0);
  const tier = useWidthTier();
  // "Give advance" before anyone is picked: pick the person, then the advance form opens.
  const [pending, setPending] = useState<MoneyMode | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const week = { from: shiftDay(todayIso(), -6), to: todayIso() };

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    refreshRoster(orgId).then(setRoster).catch(() => undefined);
  }, [role, history, orgId]);

  const load = useCallback(async () => {
    if (!personId) return;
    try {
      const res: any = await getPayAccount(personId, from, to);
      if (res?.success) setAccount(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load the account"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("The pay account needs a connection."), type: "warning" });
    }
  }, [personId, from, to]);

  useEffect(() => {
    load();
    history.replace({ search: personId ? `?person=${personId}` : "" });
  }, [load, personId, history]);

  useEffect(() => {
    if (account && pending) {
      // Not for a worker waiting for the owner's confirmation: the server refuses their money entries.
      if (!account.person?.pendingConfirmation) setMoney(pending);
      setPending(null);
    }
  }, [account, pending]);

  const askWho = (mode: MoneyMode) => {
    setPending(mode);
    pickerRef.current?.scrollIntoView({ block: "center" });
    pickerRef.current?.querySelector("input")?.focus();
  };
  const openPerson = (p: { id: string; code: string; name: string }, then?: MoneyMode) => {
    setPerson(p);
    setPersonId(p.id);
    setAccount(null);
    if (then) setPending(then);
  };

  const [takingBack, setTakingBack] = useState<any>(null);
  const [sending, setSending] = useState(false);
  const approverHere = role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER";
  const cashbook = useCashbookStatus();

  /** Owner/manager: taken back now. Supervisor: sent for approval with the spoken or written reason. */
  const takeBack = async (body: { note?: string; voiceNoteUrl?: string }) => {
    if (!takingBack) return;
    setSending(true);
    const res: any = await voidPayEntry(takingBack.id, body).catch(() => null);
    setSending(false);
    if (!res?.success) {
      notifications({ title: t("Not saved"), message: t(res?.message || "Needs a connection"), type: "danger" });
      return;
    }
    notifications({
      title: res.data.voidRequested ? t("Sent for approval") : t("Taken back"),
      message: res.data.voidRequested ? t("The owner or a manager will approve it. Until then it still counts.") : t("The account is updated."),
      type: "success",
    });
    setTakingBack(null);
    load();
  };

  if (!canUseRounds(role)) return null;
  const a = account;
  const shown = a?.person || person;

  return (
    <div className={`gi-registry-page kw-page kr-rounds kpy-page is-${tier}${!personId && tier !== "table" ? " has-foot" : ""}`}>
      <PageHeader title={t("Pay")} back={personId ? kp.pay() : undefined} backLabel={t("Back")}>
        {tier === "table" && !personId ? (
          <>
            {role === "ORGANIZATION_ADMIN" ? (
              <button type="button" className="kd-btn kd-btn--line" onClick={() => history.push(kp.payOpenings())}>
                {t("Opening balances")}
              </button>
            ) : null}
            <button type="button" className="kd-btn kd-btn--line" onClick={() => askWho("advance")}>
              {t("Give advance")}
            </button>
            <button type="button" className="kd-btn" onClick={() => history.push(kp.paySheets())}>
              {t("Make pay sheet")}
            </button>
          </>
        ) : null}
        <LangToggle />
      </PageHeader>
      {tier === "table" && !personId ? <p className="kd-sub">{t("Week {{range}}", { range: rangeLabel(week.from, week.to, lang) })}</p> : null}
      {pending && !personId ? <div className="kr-banner is-info">{t("Pick who gets the advance.")}</div> : null}

      <div ref={pickerRef}>
        <PersonPicker
          value={shown ? { id: shown.id, code: shown.code, name: shown.name } : null}
          onChange={(p) => {
            setPerson(p);
            setPersonId(p?.id || null);
            setAccount(null);
            if (!p) setPending(null);
          }}
          placeholder={t("Whose account? Code or name, e.g. K_56")}
          label={t("Person")}
          roster={roster}
        />
      </div>

      {!personId && tier === "table" ? (
        <div className="kpy-split">
          <PayWeekTable from={week.from} to={week.to} refreshKey={ledgerKey} onPay={(p) => openPerson(p, "pay")} />
          <aside className="kpy-split__side">
            <NotCounted onChanged={() => setLedgerKey((k) => k + 1)} />
            <CashCard supervisor={isKarkhanaSupervisor(role)} />
            <LastSheetCard />
          </aside>
        </div>
      ) : null}
      {!personId && tier !== "table" ? (
        <>
          <NotCounted onChanged={() => setLedgerKey((k) => k + 1)} />
          <PayLedger key={ledgerKey} onPick={(p) => openPerson(p, pending || undefined)} between={<CashCard supervisor={isKarkhanaSupervisor(role)} />} />
          {role === "ORGANIZATION_ADMIN" ? (
            <p className="mt-3">
              <button type="button" className="btn btn-link p-0" onClick={() => history.push(kp.payOpenings())}>
                {t("Opening balances")}
              </button>{" "}
              <span className="kw-muted">{t("Carry over advances and unpaid wages from the old register")}</span>
            </p>
          ) : null}
          <div className="kf-foot">
            <button type="button" className="kd-btn kd-btn--line" onClick={() => askWho("advance")}>
              {t("Give advance")}
            </button>
            <button type="button" className="kd-btn" onClick={() => history.push(kp.paySheets())}>
              {t("Pay sheet")}
            </button>
          </div>
        </>
      ) : null}
      {personId && !a ? <div className="kw-empty mt-3">{t("Loading…")}</div> : null}

      {a ? (
        <>
          <div className="kpay-balances">
            <div className={`kpay-balance${Number(a.wagesDue) > 0 ? " is-due" : ""}`}>
              <span>{t("Wages due")}</span>
              <strong>{rupees(a.wagesDue)}</strong>
            </div>
            <div className="kpay-balance">
              <span>{t("Advance owed")}</span>
              <strong>{rupees(a.advanceOutstanding)}</strong>
              {a.agreedRecovery ? <small>{rupees(a.agreedRecovery, 0)} cut each payout</small> : null}
            </div>
          </div>
          {a.opening ? (
            <p className="kw-lede kpay-opening">
              {t("Opening balance on {{day}}: due {{due}} · advance {{adv}}. Work and money before this date are in it.", {
                day: fmtDay(a.opening.date),
                due: rupees(a.opening.wagesDue),
                adv: rupees(a.opening.advance),
              })}
              {role === "ORGANIZATION_ADMIN" ? (
                <>
                  {" "}
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => history.push(kp.payOpenings())}>
                    {t("Change")}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {a.person?.pendingConfirmation ? (
            <p className="kw-lede">
              <span className="kw-chip is-review">{t("Waiting for owner")}</span>{" "}
              {t("Pay and advances wait until the owner confirms {{name}} in Workers.", { name: a.person.name || a.person.code })}
            </p>
          ) : (
            <div className="kw-toolbar">
              <Button className="ResetBtn" disabled={Number(a.wagesDue) <= 0} onClick={() => setMoney("pay")}>
                {t("Pay")}
              </Button>
              <Button outline onClick={() => setMoney("advance")}>
                {t("Give advance")}
              </Button>
              {approverHere && cashbook.loaded && cashbook.enabled && personId ? (
                <Button outline onClick={() => history.push(kp.cashWorker(personId))}>
                  {t("Worker payments")}
                </Button>
              ) : null}
            </div>
          )}

          {a.issues?.length ? (
            <div className="kr-banner is-warn">
              {t("Not counted yet:")}
              <ul className="mb-0 ps-3">
                {a.issues.slice(0, 6).map((i: any, n: number) => (
                  <li key={n}>
                    {fmtDay(i.date)}: {i.issue}
                  </li>
                ))}
              </ul>
              {a.issues.length > 6 ? t("…and {{n}} more", { n: a.issues.length - 6 }) : ""}
            </div>
          ) : null}

          <div className="kr-daybar">
            <div className="kr-daybar__nav">
              <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label="From" />
              <span className="kw-muted">to</span>
              <input type="date" value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} aria-label="To" />
            </div>
          </div>

          <h3 className="kr-group-title">
            Earned {fmtDay(a.from)} – {fmtDay(a.to)} · {rupees(a.periodEarned)}
          </h3>
          {!a.days?.length ? <div className="kw-empty">{t("No work recorded in these dates.")}</div> : null}
          {(a.days || []).map((d: any) => (
            <div key={d.date} className="kw-card kpay-day">
              <div className="kr-pay__row">
                <strong>{fmtDay(d.date)}</strong>
                <strong>{rupees(d.total)}</strong>
              </div>
              {(d.loom || []).map((l: any) => (
                <div key={l.id} className="kr-pay__row kw-muted">
                  <span>
                    <span className="kw-code">{l.loomCode}</span> · {l.workTypeCode} · {l.inches} in
                    {l.incentive ? " · incentive" : ""}
                  </span>
                  <span>{l.status === "PENDING_APPROVAL" ? "waiting" : l.amount ? rupees(l.amount) : "no rate"}</span>
                </div>
              ))}
              {d.time && d.time.minutes ? (
                <div className="kr-pay__row kw-muted">
                  <span>Time work · {hours(d.time.minutes)}</span>
                  <span>
                    {d.time.status === "OK"
                      ? rupees(d.time.amount)
                      : d.time.status === "IN_PROGRESS"
                      ? "still in"
                      : d.time.status === "WAITING"
                      ? "waiting"
                      : d.time.status === "HELD"
                      ? "missing In/Out"
                      : "no rate"}
                  </span>
                </div>
              ) : null}
              {(d.job || []).map((j: any) => (
                <div key={j.id} className="kr-pay__row kw-muted">
                  <span>
                    {t(j.activityName)} · {j.quantity} {t(UNIT_SHORT[j.unit] || j.unit)}
                    {j.sareeNumbers?.length ? ` · ${t("Saree")} ${j.sareeNumbers.join(", ")}` : ""}
                  </span>
                  <span>{j.status === "PENDING_APPROVAL" ? t("waiting") : rupees(j.amount)}</span>
                </div>
              ))}
            </div>
          ))}

          <h3 className="kr-group-title">{t("Money given")}</h3>
          {!a.entries?.length ? <div className="kw-empty">{t("Nothing paid or advanced yet.")}</div> : null}
          {(a.entries || []).map((e: any) => (
            <div key={e.id} className={`kr-pay__row kpay-entry is-${e.kind}`}>
              <span>
                <strong>{t(KIND_LABEL[e.kind] || e.kind)}</strong> · {fmtDay(e.date)}
                {e.method ? ` · ${e.method}` : ""}
                {e.recoveryPerPay ? ` · ${t("cut {{amount}} each payout", { amount: rupees(e.recoveryPerPay, 0) })}` : ""}
                {e.note ? ` · “${e.note}”` : ""}
                <span className="kw-muted"> · {e.recordedByName}</span>
              </span>
              <span>
                {e.kind === "OPENING" ? `${t("due")} ${rupees(e.amount)} · ${t("advance")} ${rupees(e.openingAdvance || 0)}` : rupees(e.amount)}{" "}
                {e.kind === "OPENING" ? null : a.opening && e.date < a.opening.date ? (
                  <span className="kw-chip">{t("In the opening")}</span>
                ) : e.voidRequested ? (
                  <span className="kw-chip is-review">{t("Take-back waiting for approval")}</span>
                ) : (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setTakingBack(e)}>
                    {t("Undo")}
                  </button>
                )}
                {e.voidDecision === "REJECTED" ? <span className="kw-chip ms-1">{t("Take-back turned down")}</span> : null}
              </span>
            </div>
          ))}
        </>
      ) : null}

      <TakeBackDialog entry={takingBack} approverHere={approverHere} busy={sending} onCancel={() => setTakingBack(null)} onSend={takeBack} />

      {money && a ? (
        <MoneySheet
          mode={money}
          account={a}
          onClose={() => setMoney(null)}
          onSaved={() => {
            setMoney(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
};

export default KarkhanaPayPage;
